import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from '@fs/zion-ui'
import { timestampToDateString } from '../../../../util/DateUtil'
import useCompactQueueEventListeners from './useCompactQueueEventListeners'
import { CmisLink, MrnLink, PersonLink, RelationshipLink, TreeLink, TreePersonLink, UserLink } from '../../../app/Links'
import DmcCopyToClipboardTooltip from '../../../../util/DmcCopyToClipboardTooltip'
import { printValueOrBooleanReadable } from '../../../../util/StringUtil'

export default function useCompactMessagesListController({ queueState, queueActions, queueCachesState, filterRef }) {
  const [shiftIsPressed, setShiftIsPressed] = useState(false)
  const [activeColumns, setActiveColumns] = useState({ columnList: [] })
  const [contextMenu, setContextMenu] = useState(null)
  const [lastCheckedIndex, setLastCheckedIndex] = useState()

  useCompactQueueEventListeners({
    queueState,
    queueActions,
    contextMenu,
    setContextMenu,
    setShiftIsPressed,
    setLastCheckedIndex,
    filterRef,
  })
  const { t } = useTranslation()

  const queueColumns = useMemo(() => {
    const keys = Array.from(
      new Set(
        queueState?.queueContents?.flatMap((message) =>
          Object.keys(message.details).filter((key) => key !== 'changeId')
        )
      )
    )
    const priority = [
      'name',
      'nameFromMembership',
      'personId',
      'fatherPersonId',
      'motherPersonId',
      'searchPersonId',
      'matchedPersonId',
      'treePersonId',
    ]
    keys.sort((a, b) => {
      const indexA = priority.indexOf(a) === -1 ? 99 : priority.indexOf(a)
      const indexB = priority.indexOf(b) === -1 ? 99 : priority.indexOf(b)
      return indexA - indexB
    })

    return ['created', ...(keys || [])]
  }, [queueState.queueContents])

  const columnMultiSelect = (column) => {
    let start
    let end
    if (activeColumns.lastCheckedIndex < queueColumns.indexOf(column)) {
      start = activeColumns.lastCheckedIndex
      end = queueColumns.indexOf(column)
    } else {
      start = queueColumns.indexOf(column) - 1
      end = activeColumns.lastCheckedIndex - 1
    }
    const updatedCheckboxes = queueColumns.slice(start + 1, end + 1).reduce(
      (acc, col) => {
        return acc.includes(queueColumns[end]) ? acc.filter((c) => c !== col) : [...acc, col]
      },
      [...activeColumns.columnList]
    )

    const excludedFields = queueColumns.filter((col) => !updatedCheckboxes.includes(col))
    localStorage.setItem(`excludedFields_${queueState.queueName}`, JSON.stringify(excludedFields))

    setActiveColumns({
      columnList: updatedCheckboxes,
      lastCheckedIndex: queueColumns.indexOf(column),
    })
  }

  const toggleColumn = (column) => {
    setActiveColumns((prev) => {
      const updatedColumns = prev.columnList.includes(column)
        ? prev.columnList.filter((col) => col !== column)
        : queueColumns.filter((col) => [...prev.columnList, column].includes(col))
      localStorage.setItem(
        `excludedFields_${queueState.queueName}`,
        JSON.stringify(queueColumns.filter((col) => !updatedColumns.includes(col)))
      )
      return { columnList: updatedColumns, lastCheckedIndex: queueColumns.indexOf(column) }
    })
  }

  const handleColumnCheckboxChange = (column) => {
    if (shiftIsPressed && activeColumns.lastCheckedIndex !== undefined) {
      columnMultiSelect(column)
    } else {
      setActiveColumns(() => {
        toggleColumn(column)
      })
    }
  }

  const rowMultiSelect = (clickedIndex, event) => {
    // Handle range selection within the filtered list
    const start = Math.min(clickedIndex, lastCheckedIndex)
    const end = Math.max(clickedIndex, lastCheckedIndex)

    for (let i = start; i <= end; i++) {
      queueActions.addCheckboxSelection(queueState.queueContents[i], event.target.checked)
    }
  }

  const handleRowCheckboxChange = (event) => {
    const clickedIndex = parseInt(event.target.name, 10)

    if (shiftIsPressed && lastCheckedIndex !== undefined) {
      rowMultiSelect(clickedIndex, event)
    } else {
      queueActions.addCheckboxSelection(queueState.queueContents[clickedIndex], event.target.checked)
    }

    setLastCheckedIndex(clickedIndex)
  }

  const formatField = (message, key = queueState.sortField, memoriesLink = false) => {
    const details = message.details || {}
    let result = {}

    switch (key) {
      case 'created':
        result = {
          value: timestampToDateString({
            timestamp: message.inQueueSince,
            withTime: true,
            timestampIsSeconds: true,
          }),
          sortValue: message.inQueueSince,
        }
        break

      case 'personId':
      case 'fatherPersonId':
      case 'motherPersonId':
      case 'searchPersonId':
      case 'matchedPersonId':
      case 'treePersonId':
      case 'person1Id':
      case 'person2Id':
        result = {
          filterValue: details[key],
          value: (
            <Row>
              <DmcCopyToClipboardTooltip
                value={details[key]}
                body={<PersonLink personId={details[key]} newTab />}
                nextToText
              />
              <DmcCopyToClipboardTooltip
                value={details[key]}
                body={<TreePersonLink personId={details[key]} treeIcon newTab memories={memoriesLink} />}
                nextToText
              />
            </Row>
          ),
        }
        break

      case 'coupleId':
      case 'associationId':
      case 'parent_childId': {
        const relationships = {
          coupleId: 'couple',
          associationId: 'association',
          parent_childId: 'parentchild',
        }
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<RelationshipLink relationshipId={details[key]} relationshipType={relationships[key]} newTab />}
              nextToText
            />
          ),
        }
        break
      }

      case 'contributorId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={
                <UserLink
                  userName={queueCachesState.usersCache[details[key]]?.contactName}
                  userId={details[key]}
                  newTab
                />
              }
              nextToText
            />
          ),
        }
        break

      case 'mrn':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip value={details[key]} body={<MrnLink mrn={details[key]} newTab />} nextToText />
          ),
        }
        break

      case 'cmisId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<CmisLink cmisId={details[key]} newTab />}
              nextToText
            />
          ),
        }
        break

      case 'treeId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<TreeLink treeId={details[key]} newTab />}
              nextToText
            />
          ),
        }
        break

      case 'aiScores': {
        const aiScores = details.aiScores
        const aiCategory = aiScores?.categoryScores?.find((cat) => cat.personType === aiScores?.personType)
        if (aiCategory?.value != null) {
          const score = Math.floor((aiCategory.value * 100) / 5) * 5
          if (score)
            result = {
              value: t(`queues.aiCategoryScore.${aiScores?.personType}`, '{type}: {score}', {
                type: aiCategory?.personType,
                score: Math.floor((aiCategory.value * 100) / 5) * 5,
              }),
            }
        }
        break
      }

      case 'duplicatePersonId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<PersonLink personId={details[key]} newTab forward={false} />}
              nextToText
            />
          ),
        }
        break

      default:
        if (message.details && typeof message.details[key] === 'object') {
          result = { value: JSON.stringify(message.details[key], null, 2) }
        } else {
          result = { value: printValueOrBooleanReadable(details[key]) || '' }
        }
        break
    }
    return result
  }

  const sortIconOnClick = (type, field) => {
    queueState.sortDirection = type
    queueState.sortField = field
    queueActions.setQueueState((prevState) => ({
      ...prevState,
      sortDirection: type,
      sortField: field,
    }))
  }

  const sortByFieldType = (field) => {
    if (field !== queueState.sortField) {
      sortIconOnClick('asc', field)
    } else if (queueState.sortDirection === undefined) {
      sortIconOnClick('asc', field)
    } else if (queueState.sortDirection === 'asc') {
      sortIconOnClick('desc', field)
    } else if (queueState.sortDirection === 'desc') {
      sortIconOnClick(undefined, undefined)
    }
  }

  useEffect(() => {
    const savedExcludedFields = localStorage.getItem(`excludedFields_${queueState.queueName}`)
    const excludedFields = JSON.parse(savedExcludedFields)

    if (queueState.queueContents.length > 0) {
      setActiveColumns({
        columnList: queueColumns.filter((col) => !excludedFields?.includes(col)),
        lastCheckedIndex: 0,
      })
    }
  }, [queueState.queueContents, queueState.queueName, t, queueColumns])

  // Contributor suggestions for Autosuggest

  return {
    compactQueueState: {
      queueColumns,
      shiftIsPressed,
      activeColumns,
      contextMenu,
    },
    compactQueueActions: {
      setShiftIsPressed,
      setActiveColumns,
      handleColumnCheckboxChange,
      handleRowCheckboxChange,
      formatField,
      sortByFieldType,
    },
  }
}
